import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Quote, Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REACTIONS = [
  { tipo: 'like',   emoji: '❤️', label: 'Me gusta' },
  { tipo: 'fire',   emoji: '🔥', label: 'Fuego' },
  { tipo: 'muscle', emoji: '💪', label: 'Bestia' },
  { tipo: 'lion',   emoji: '🦁', label: 'Lobo MK' },
  { tipo: 'clap',   emoji: '👏', label: 'Aplauso' },
];

/**
 * Frase del día — rota aleatoriamente. Ahora se puede reaccionar con emoji.
 */
export default function QuoteOfTheDay() {
  const [quote, setQuote] = useState(null);
  const [reacciones, setReacciones] = useState({ like: [], fire: [], muscle: [], lion: [], clap: [] });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    // Recuperar user_id del localStorage (guardado por AuthContext al hacer login)
    try {
      const stored = localStorage.getItem('user');
      if (stored) setCurrentUserId(JSON.parse(stored).id);
    } catch { /* ignore */ }

    let cancelled = false;
    axios.get(`${API}/stoic-quotes/today`)
      .then(res => {
        if (cancelled) return;
        setQuote(res.data);
        if (res.data?.reacciones) setReacciones({ like: [], fire: [], muscle: [], lion: [], clap: [], ...res.data.reacciones });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const toggleReaccion = async (tipo) => {
    if (!quote?.id) return;
    try {
      const res = await axios.post(`${API}/stoic-quotes/${quote.id}/reaccion`, { tipo });
      if (res.data?.reacciones) setReacciones({ like: [], fire: [], muscle: [], lion: [], clap: [], ...res.data.reacciones });
    } catch { /* ignore */ }
    setPickerOpen(false);
  };

  if (!quote || !quote.frase) return null;

  const totalCount = REACTIONS.reduce((acc, r) => acc + (reacciones[r.tipo] || []).length, 0);
  const myReactions = REACTIONS.filter(r => (reacciones[r.tipo] || []).some(x => x.user_id === currentUserId));
  const topReactions = REACTIONS
    .map(r => ({ ...r, count: (reacciones[r.tipo] || []).length }))
    .filter(r => r.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  return (
    <div
      className="quote-of-day-card mt-3 max-w-md bg-gradient-to-br from-gray-900/80 to-gray-800/60 border-l-4 border-gold rounded-r-lg px-3 py-2 backdrop-blur-sm relative"
      data-testid="quote-of-the-day"
    >
      <div className="flex items-start gap-2">
        <Quote className="quote-of-day-icon h-4 w-4 text-gold flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="quote-of-day-text text-white text-sm sm:text-base italic leading-snug">
            &ldquo;{quote.frase}&rdquo;
          </p>
          {quote.autor && (
            <p className="quote-of-day-author text-gold/70 text-[11px] sm:text-xs mt-1 uppercase tracking-wide font-semibold">
              — {quote.autor}
            </p>
          )}

          {/* Reacciones */}
          <div className="flex items-center gap-2 mt-2 flex-wrap relative">
            {topReactions.length > 0 && (
              <div className="flex items-center gap-1" data-testid="quote-reactions-summary">
                {topReactions.map(r => {
                  const mine = (reacciones[r.tipo] || []).some(x => x.user_id === currentUserId);
                  return (
                    <button
                      key={r.tipo}
                      onClick={() => toggleReaccion(r.tipo)}
                      className={`flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full transition-colors ${mine ? 'bg-gold/20 border border-gold/40' : 'bg-gray-800/60 border border-gray-700 hover:bg-gray-700'}`}
                      data-testid={`quote-react-chip-${r.tipo}`}
                    >
                      <span className="text-sm leading-none">{r.emoji}</span>
                      <span className="text-[10px] text-gray-300">{r.count}</span>
                    </button>
                  );
                })}
                {totalCount > 0 && (
                  <span className="text-[10px] text-gray-500 ml-1">{totalCount}</span>
                )}
              </div>
            )}

            <button
              onClick={() => setPickerOpen(o => !o)}
              className={`flex items-center gap-1 text-xs transition-colors p-1 -m-1 rounded-lg ${myReactions.length > 0 ? 'text-gold' : 'text-gray-400 hover:text-gold'}`}
              data-testid="quote-btn-react"
              title={myReactions.length > 0 ? `Has reaccionado con ${myReactions.map(r => r.emoji).join('')}` : 'Reaccionar a esta frase'}
            >
              {myReactions.length > 0 ? (
                <span className="text-base leading-none">{myReactions.map(r => r.emoji).join('')}</span>
              ) : (
                <>
                  <Heart className="w-4 h-4" />
                  <span className="text-[10px]">Reaccionar</span>
                </>
              )}
            </button>

            {pickerOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setPickerOpen(false)} />
                <div className="absolute top-full mt-1 left-0 z-30 bg-gray-900 border border-gray-700 rounded-full px-2 py-1.5 flex items-center gap-1 shadow-2xl" data-testid="quote-reaction-picker">
                  {REACTIONS.map(r => {
                    const mine = (reacciones[r.tipo] || []).some(x => x.user_id === currentUserId);
                    return (
                      <button
                        key={r.tipo}
                        onClick={() => toggleReaccion(r.tipo)}
                        className={`text-xl hover:scale-125 transition-transform p-1 ${mine ? 'opacity-100 ring-2 ring-gold rounded-full' : 'opacity-80 hover:opacity-100'}`}
                        title={r.label}
                        data-testid={`quote-react-pick-${r.tipo}`}
                      >
                        {r.emoji}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
